import { useState, useEffect, useCallback } from 'react';
import api from '@/api';
import { RefreshCw, CheckCircle, AlertCircle, Github, Database, Code, X, ChevronDown, ChevronUp } from 'lucide-react';

/**
 * SyncStatusBar - Shows real-time sync/indexing progress in the UI
 * Polls backend endpoints to display current processing status
 */
export default function SyncStatusBar() {
  const [syncStatus, setSyncStatus] = useState({
    github: { syncing: false, indexing: false },
    isActive: false
  });
  const [expanded, setExpanded] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [details, setDetails] = useState(null);

  const fetchSyncStatus = useCallback(async () => {
    try {
      // Fetch GitHub sync status
      const [githubStatus, indexStatus] = await Promise.all([
        api.get('/github/status').catch(() => ({ data: { sync_in_progress: false } })),
        api.get('/github/index/status').catch(() => ({ data: { indexing: { is_indexing: false } } }))
      ]);

      const githubSyncing = githubStatus.data?.sync_in_progress || false;
      const githubIndexing = indexStatus.data?.indexing?.is_indexing || false;
      const isActive = githubSyncing || githubIndexing;

      // Build details for expanded view
      const newDetails = {
        github: {
          syncing: githubSyncing,
          indexing: githubIndexing,
          metrics: githubStatus.data?.metrics || {},
          indexProgress: indexStatus.data?.indexing || {}
        }
      };

      setSyncStatus({
        github: { syncing: githubSyncing, indexing: githubIndexing },
        isActive
      });
      setDetails(newDetails);

      // Auto-show when activity starts, reset dismissed state
      if (isActive && dismissed) {
        setDismissed(false);
      }
    } catch (err) {
      console.error('Error fetching sync status:', err);
    }
  }, [dismissed]);

  // Poll for status updates
  useEffect(() => {
    fetchSyncStatus();

    // Poll more frequently when active, less when idle
    const interval = setInterval(fetchSyncStatus, syncStatus.isActive ? 2000 : 10000);

    return () => clearInterval(interval);
  }, [fetchSyncStatus, syncStatus.isActive]);

  // Don't render if no activity or dismissed
  if (!syncStatus.isActive || dismissed) {
    return null;
  }

  const getStatusMessage = () => {
    const messages = [];

    if (syncStatus.github.syncing) {
      messages.push('Syncing GitHub data');
    }
    if (syncStatus.github.indexing) {
      const progress = details?.github?.indexProgress;
      if (progress?.current_repo) {
        messages.push(`Indexing ${progress.current_repo}`);
      } else {
        messages.push('Indexing code');
      }
    }

    return messages.length > 0 ? messages.join(' • ') : 'Processing...';
  };

  const getProgressPercent = () => {
    if (details?.github?.indexProgress?.is_indexing) {
      return details.github.indexProgress.progress_percent || 0;
    }
    return null;
  };

  const progressPercent = getProgressPercent();

  return (
    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-blue-200">
      {/* Main status bar */}
      <div className="px-4 py-2 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <RefreshCw className="w-4 h-4 text-blue-600 animate-spin" />
            <span className="text-sm font-medium text-blue-700">
              {getStatusMessage()}
            </span>
          </div>

          {/* Progress percentage */}
          {progressPercent !== null && (
            <span className="text-xs text-blue-500 bg-blue-100 px-2 py-0.5 rounded-full">
              {progressPercent}%
            </span>
          )}
        </div>

        <div className="flex items-center gap-2">
          {/* Expand/collapse button */}
          <button
            onClick={() => setExpanded(!expanded)}
            className="p-1 text-blue-500 hover:text-blue-700 hover:bg-blue-100 rounded transition-colors"
            title={expanded ? 'Collapse' : 'Show details'}
          >
            {expanded ? (
              <ChevronUp className="w-4 h-4" />
            ) : (
              <ChevronDown className="w-4 h-4" />
            )}
          </button>

          {/* Dismiss button */}
          <button
            onClick={() => setDismissed(true)}
            className="p-1 text-blue-400 hover:text-blue-600 hover:bg-blue-100 rounded transition-colors"
            title="Dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Progress bar */}
      {progressPercent !== null && (
        <div className="h-1 bg-blue-100">
          <div
            className="h-full bg-blue-500 transition-all duration-500 ease-out"
            style={{ width: `${progressPercent}%` }}
          />
        </div>
      )}

      {/* Expanded details */}
      {expanded && details && (
        <div className="px-4 py-3 bg-white/50 border-t border-blue-100 space-y-2">
          {/* GitHub Status */}
          {(syncStatus.github.syncing || syncStatus.github.indexing) && (
            <div className="flex items-start gap-3">
              <Github className="w-4 h-4 text-gray-700 mt-0.5" />
              <div className="flex-1">
                <div className="text-sm font-medium text-gray-700">GitHub</div>
                <div className="text-xs text-gray-500 space-y-1">
                  {syncStatus.github.syncing && (
                    <div className="flex items-center gap-2">
                      <Database className="w-3 h-3" />
                      <span>Syncing PRs, issues, and workflow runs...</span>
                    </div>
                  )}
                  {syncStatus.github.indexing && details.github.indexProgress && (
                    <div className="flex items-center gap-2">
                      <Code className="w-3 h-3" />
                      <span>
                        Indexing code: {details.github.indexProgress.completed_repos || 0} / {details.github.indexProgress.total_repos || 0} repos
                        {details.github.indexProgress.current_repo && (
                          <span className="text-blue-600 ml-1">
                            ({details.github.indexProgress.current_repo})
                          </span>
                        )}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Sync metrics if available */}
          {details.github.metrics?.last_sync_at && (
            <div className="text-xs text-gray-400 pl-7">
              Last sync: {new Date(details.github.metrics.last_sync_at).toLocaleString()}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
